import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useConfigurationStore, useDeviceStore } from '@/stores'
import {
  ReportEvents,
  type DPIDirect,
  type GearType,
  type mouseUserCustom,
} from '@rapoo/device-sdk'
import { reportMouseSdkError } from './reportMouseSdkError'

/**
 * 本次连接内的 DPI 快照。
 * 页面随路由销毁，快照留在模块里，同一设备、同一配置档再次进入时先画这份数据。
 */
interface DpiSnapshot {
  gears: GearType[]
  currentGearId: string
}

type DpiPhase = 'cold' | 'ready' | 'error'

const dpiSnapshots = new Map<string, DpiSnapshot>()

function dpiSnapshotKey(deviceId: string, configIndex: number) {
  return `${deviceId}:${configIndex}`
}

function cloneGears(gears: GearType[]): GearType[] {
  return gears.map((gear) => ({ ...gear }))
}

function gearsEqual(left: GearType[], right: GearType[]) {
  if (left.length !== right.length) return false
  return left.every((gear, index) => {
    const other = right[index]
    return (
      gear.id === other.id &&
      gear.x === other.x &&
      gear.y === other.y &&
      gear.independentXY === other.independentXY &&
      gear.enable === other.enable
    )
  })
}

function readSnapshot(deviceId: string, configIndex: number): DpiSnapshot | undefined {
  const snap = dpiSnapshots.get(dpiSnapshotKey(deviceId, configIndex))
  if (!snap) return undefined
  return {
    gears: cloneGears(snap.gears),
    currentGearId: snap.currentGearId,
  }
}

function writeSnapshot(deviceId: string, configIndex: number, gears: GearType[], gearId: string) {
  dpiSnapshots.set(dpiSnapshotKey(deviceId, configIndex), {
    gears: cloneGears(gears),
    currentGearId: gearId,
  })
}

export const useDPI = () => {
  // 档位始终为7个，已删除的根据 enbaledGearCount 从后开始剔除
  const dpiGears = ref<GearType[]>([])
  const currentGearId = shallowRef('')
  const enabledGearCount = shallowRef(0) // 启用的档位数量
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const configStore = useConfigurationStore()
  const { currentConfig } = storeToRefs(configStore)
  /** 冷启动还在读：出骨架。已有快照时保持 ready，回读不再把它打回骨架。 */
  const phase = shallowRef<DpiPhase>('cold')
  const isReading = shallowRef(false)
  const isReady = shallowRef(false)
  /** 回读和首次挂载期间为 false，滑块只在这之后的用户操作和设备上报里过渡。 */
  const animateValueChanges = shallowRef(false)

  let initRequestId = 0
  let mutationEpoch = 0
  let listeningDevice: typeof currentDevice.value = null

  const configIndex = computed(() => currentConfig.value?.index || 0)

  function rememberCurrent() {
    const device = currentDevice.value
    if (!device || dpiGears.value.length === 0 || !currentGearId.value) return
    writeSnapshot(device.id, configIndex.value, dpiGears.value, currentGearId.value)
  }

  /** 本地写入或设备上报比进行中的回读更新，回读结果不再覆盖界面。 */
  function noteLocalChange() {
    mutationEpoch += 1
    rememberCurrent()
  }

  function showCold() {
    animateValueChanges.value = false
    isReady.value = false
    phase.value = 'cold'
    dpiGears.value = []
    currentGearId.value = ''
  }

  /** 有快照时在第一次渲染前灌进界面。内容没变时不换对象，避免滑块再走一遍 watch。 */
  function hydrate(deviceId: string, index: number) {
    const snap = readSnapshot(deviceId, index)
    if (!snap || snap.gears.length === 0) return false

    const changed =
      phase.value !== 'ready' ||
      currentGearId.value !== snap.currentGearId ||
      !gearsEqual(dpiGears.value, snap.gears)

    if (changed) {
      animateValueChanges.value = false
      dpiGears.value = snap.gears
      currentGearId.value = snap.currentGearId
    }

    phase.value = 'ready'
    isReady.value = true
    return true
  }

  async function commitDisplay(
    deviceId: string,
    targetConfigIndex: number,
    gears: GearType[],
    gearId: string,
    isCurrent: () => boolean,
  ) {
    if (!isCurrent()) return

    writeSnapshot(deviceId, targetConfigIndex, gears, gearId)
    const changed = currentGearId.value !== gearId || !gearsEqual(dpiGears.value, gears)
    phase.value = 'ready'
    isReady.value = true

    if (!changed) {
      animateValueChanges.value = true
      return
    }

    // 先关掉过渡再写入，下一帧再打开。滑块这次赋值不会从旧值滑到新值。
    animateValueChanges.value = false
    dpiGears.value = cloneGears(gears)
    currentGearId.value = gearId
    await nextTick()
    if (!isCurrent()) return
    animateValueChanges.value = true
  }

  /**
   * 设置当前 DPI 档位索引
   * @param id 目标档位id
   * @param configIndex 配置文件索引
   */
  const setCurrentGear = async (index: number): Promise<void> => {
    const dpi = currentDevice.value?.features.dpi
    const gear = dpiGears.value[index]

    if (!dpi) {
      throw new Error("Current device doesn't have dpi feature")
    }
    if (!isReady.value || !gear) return

    try {
      currentGearId.value = gear.id
      noteLocalChange()
      await dpi.setDeviceGear(index, configIndex.value)
    } catch (err) {
      console.error('Failed to get current dpi gear', err)
      reportMouseSdkError('Failed to get current dpi gear', err)
    }
  }

  /**
   * 设置当前档位 DPI
   * @param index 目标档位索引
   * @param value 目标档位 DPI
   * @param direct 当前设置的是 x 还是 y
   */
  const setGearDPI = async (index: number, value: number, direct: DPIDirect): Promise<void> => {
    const dpi = currentDevice.value?.features.dpi

    if (!dpi) {
      throw new Error("Current device doesn't have dpi feature")
    }
    if (!isReady.value || !dpiGears.value[index]) return

    try {
      const requestConfigIndex = configIndex.value
      // 页面只负责即时呈现；Telink 3228C 的慢速分包合并由 SDK 设备层统一处理。
      dpiGears.value[index] = { ...dpiGears.value[index], [direct]: value }
      noteLocalChange()
      await dpi.setDeviceDPI(index, value, direct, requestConfigIndex)
    } catch (err) {
      console.error('Failed to set current gear dpi', err)
      reportMouseSdkError('Failed to set current gear dpi', err)
    }
  }

  /**
   * 设置所有档位配置，用于新增和删除
   */
  const setGears = async (gears: GearType[]): Promise<void> => {
    const dpi = currentDevice.value?.features.dpi

    if (!dpi) {
      throw new Error("Current device doesn't have dpi feature")
    }
    if (!isReady.value || dpiGears.value.length === 0) return

    try {
      dpiGears.value = [...gears]
      noteLocalChange()
      await dpi.setDeviceGears(gears, configIndex.value)
    } catch (err) {
      console.error('Failed to set dpi gears', err)
      reportMouseSdkError('Failed to set dpi gears', err)
    }
  }

  const refresh = async () => {
    const requestId = ++initRequestId
    const epochAtStart = mutationEpoch
    const device = currentDevice.value
    const dpi = device?.features.dpi
    const targetConfigIndex = configIndex.value
    const isCurrent = () =>
      requestId === initRequestId &&
      device === currentDevice.value &&
      targetConfigIndex === configIndex.value

    try {
      if (!device || !dpi) {
        showCold()
        return
      }

      const hasSnapshot = hydrate(device.id, targetConfigIndex)
      if (!hasSnapshot) {
        showCold()
        isReading.value = true
      } else if (!animateValueChanges.value) {
        // 快照已经画上，等这一帧提交后再允许过渡，接着才去回读。
        await nextTick()
        if (!isCurrent()) return
        animateValueChanges.value = true
      }

      if (!isCurrent()) return

      const gears = await dpi.getGears(targetConfigIndex)
      const gear = await dpi.getCurrentGear(targetConfigIndex)

      if (!isCurrent() || epochAtStart !== mutationEpoch) return

      const enabledGears = gears.filter((item) => item.enable)
      const selected = enabledGears[gear] ?? enabledGears[0]
      if (!selected) {
        throw new Error('Device returned no enabled DPI gears')
      }

      if (!enabledGears[gear]) {
        const err = new Error(
          `Invalid current DPI gear index ${gear}; enabled gear count is ${enabledGears.length}`,
        )
        console.error(err)
        reportMouseSdkError('Invalid current DPI gear index', err)
      }

      await commitDisplay(device.id, targetConfigIndex, enabledGears, selected.id, isCurrent)
    } catch (err) {
      if (!isCurrent()) return
      console.error('Failed to initialize DPI gears', err)
      reportMouseSdkError('Failed to initialize DPI gears', err)
      if (phase.value !== 'ready') {
        phase.value = 'error'
        isReady.value = false
      }
    } finally {
      if (requestId === initRequestId) isReading.value = false
    }
  }

  /**
   * 监听设备上报的配置变更事件 (鼠标物理操作切换DPI档位)
   * 当用户通过鼠标按键切换DPI时，设备会上报 report:Dpi_Gear_changed 事件
   * 这里根据上报的 cpiLev 更新当前选中的档位和DPI值
   */
  const onCfgChanged = (dpiGear: Pick<mouseUserCustom, 'cpiLev' | 'xCpiVal' | 'yCpiVal'>) => {
    const { cpiLev, xCpiVal, yCpiVal } = dpiGear

    // 更新当前档位
    if (cpiLev < dpiGears.value.length) {
      currentGearId.value = dpiGears.value[cpiLev].id

      // 同步上报的DPI值到对应档位
      dpiGears.value[cpiLev] = {
        ...dpiGears.value[cpiLev],
        x: xCpiVal,
        y: yCpiVal,
      }
      noteLocalChange()
    }
  }

  /**
   * 注册设备DPI变更上报监听
   */
  const startListenDPIReport = () => {
    const device = currentDevice.value
    if (!device || listeningDevice === device) return

    listeningDevice?.off(ReportEvents.DpiGearChanged, onCfgChanged)
    device.on(ReportEvents.DpiGearChanged, onCfgChanged)
    listeningDevice = device
  }

  /**
   * 销毁设备DPI变更上报监听
   */
  const stopListenDPIReport = () => {
    listeningDevice?.off(ReportEvents.DpiGearChanged, onCfgChanged)
    listeningDevice = null
  }

  if (currentDevice.value?.features.dpi) {
    const restored = hydrate(currentDevice.value.id, configIndex.value)
    if (!restored) isReading.value = true
  }

  onMounted(() => {
    startListenDPIReport()
    void refresh()
  })

  onBeforeUnmount(() => {
    initRequestId++
    stopListenDPIReport()
  })

  watch(currentDevice, () => {
    startListenDPIReport()
    void refresh()
  })

  watch(configIndex, (index, oldIndex) => {
    if (index === oldIndex) return
    void refresh()
  })

  return {
    dpiGears,
    currentGearId,
    enabledGearCount,
    phase,
    isReading,
    isReady,
    animateValueChanges,
    setCurrentGear,
    setGearDPI,
    setGears,
    startListenDPIReport,
    stopListenDPIReport,
  }
}
